import { ChartOptions } from 'chart.js';
import { useStore } from 'hooks/useStore';
import { observer } from 'mobx-react';
import React, { useMemo, useRef } from 'react';
import {
  type DateRange,
  type MarketSalesTrendItem,
  applyDateFilter,
} from '../../bidAnalysisTemplate.logic';
import classes from '../../bidAnalysisTemplate.module.scss';
import { MarketShareChart } from '../charts/MarketShareChart';
import { MarketTrendsChart } from '../charts/MarketTrendsChart';
import { VolumeByCotChart } from '../charts/VolumeByCotChart';
import { SubSectionHeader } from '../shared/SubSectionHeader';

interface CompetitiveAnalysisSectionProps {
  isExpanded: boolean;
  onToggle: () => void;
  bidId: number;
  concept: string;
  packSize: number;
  chartDateRange: DateRange;
  tempDateRange: DateRange;
  calendarViewDates: { startViewDate: Date | null; endViewDate: Date | null };
  selectedCompany: string | null;
  isLoadingMarketTrends: boolean;
  isLoadingMarketShare?: boolean;
  volumeByCot?: Array<{ cot: string; volume: number }>;
  isLoadingVolumeByCot?: boolean;
  chartOptions: ChartOptions;
  onDateRangeChange: (startDate: Date | null, endDate: Date | null) => void;
  onApplyFilter: () => void;
  onCalendarViewDateChange: (dates: {
    startViewDate: Date | null;
    endViewDate: Date | null;
  }) => void;
  onMarketShareClick: (vendor: string) => void;
  onChartModalOpen?: () => void;
}

export const CompetitiveAnalysisSection: React.FC<CompetitiveAnalysisSectionProps> = observer(
  ({
    isExpanded,
    onToggle,
    bidId,
    concept,
    packSize,
    chartDateRange,
    tempDateRange,
    calendarViewDates,
    selectedCompany,
    isLoadingMarketTrends,
    isLoadingMarketShare = false,
    volumeByCot = [],
    isLoadingVolumeByCot = false,
    chartOptions,
    onDateRangeChange,
    onApplyFilter,
    onCalendarViewDateChange,
    onMarketShareClick,
    onChartModalOpen,
  }) => {
    const { BidAnalysisStore } = useStore();

    // Refs to preserve last known good data to prevent graphs from disappearing
    const lastMarketShareByVolumeRef = useRef<Array<{ vendor: string; share: number }>>([]);
    const lastMarketSalesTrendDataRef = useRef<MarketSalesTrendItem[]>([]);

    // Get market sales trend data from store, preserving previous value if store is temporarily empty
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const storeMarketSalesTrendData =
      BidAnalysisStore.getMarketSalesTrendData(bidId, concept, packSize, selectedCompany) || [];
    const marketSalesTrendData = useMemo(() => {
      // If store has data, use it and update ref
      if (storeMarketSalesTrendData && storeMarketSalesTrendData.length > 0) {
        lastMarketSalesTrendDataRef.current = storeMarketSalesTrendData;
        return storeMarketSalesTrendData;
      }
      // If store is empty but we have previous data, preserve it to prevent graph from disappearing
      if (lastMarketSalesTrendDataRef.current && lastMarketSalesTrendDataRef.current.length > 0) {
        return lastMarketSalesTrendDataRef.current;
      }
      // Otherwise return empty array
      return [];
    }, [storeMarketSalesTrendData]);

    // Get market share by volume data from store, preserving previous value if store is temporarily empty

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const storeMarketShareByVolume =
      BidAnalysisStore.getMarketShareByVolumeData(bidId, concept, packSize) || [];
    const marketShareByVolume = useMemo(() => {
      // If store has data, use it and update ref
      if (storeMarketShareByVolume && storeMarketShareByVolume.length > 0) {
        lastMarketShareByVolumeRef.current = storeMarketShareByVolume;
        return storeMarketShareByVolume;
      }
      // If store is empty but we have previous data, preserve it to prevent graph from disappearing
      if (lastMarketShareByVolumeRef.current && lastMarketShareByVolumeRef.current.length > 0) {
        return lastMarketShareByVolumeRef.current;
      }
      // Otherwise return empty array
      return [];
    }, [storeMarketShareByVolume]);

    // Filter market sales trend data based on date range
    // Also preserve filtered data to prevent chart from disappearing
    const lastFilteredMarketSalesTrendDataRef = useRef<MarketSalesTrendItem[]>([]);
    const filteredMarketSalesTrendData = useMemo(() => {
      let filtered: MarketSalesTrendItem[] = [];
      if (chartDateRange.startDate || chartDateRange.endDate) {
        filtered = applyDateFilter(marketSalesTrendData, chartDateRange);
      } else {
        filtered = marketSalesTrendData;
      }

      // If filtered data has items, update ref and return
      if (filtered && filtered.length > 0) {
        lastFilteredMarketSalesTrendDataRef.current = filtered;
        return filtered;
      }

      // If filtered data is empty but we have previous filtered data, preserve it
      if (
        lastFilteredMarketSalesTrendDataRef.current &&
        lastFilteredMarketSalesTrendDataRef.current.length > 0
      ) {
        // Re-apply filter to preserved data if date range is still active
        if (chartDateRange.startDate || chartDateRange.endDate) {
          const reFiltered = applyDateFilter(
            lastFilteredMarketSalesTrendDataRef.current,
            chartDateRange
          );
          if (reFiltered && reFiltered.length > 0) {
            return reFiltered;
          }
        }
        return lastFilteredMarketSalesTrendDataRef.current;
      }

      return filtered;
    }, [marketSalesTrendData, chartDateRange]);
    return (
      <div className={classes.subSection}>
        <SubSectionHeader title="Competitive Insight" isExpanded={isExpanded} onToggle={onToggle} />
        {isExpanded && (
          <div className={classes.subSectionContent}>
            {/* Row 1: Market Sales Trends by Agency — full width */}
            <div className={classes.chartsGridFull}>
              <MarketTrendsChart
                marketSalesTrendData={marketSalesTrendData}
                filteredMarketSalesTrendData={filteredMarketSalesTrendData}
                chartDateRange={chartDateRange}
                tempDateRange={tempDateRange}
                calendarViewDates={calendarViewDates}
                selectedCompany={selectedCompany}
                isLoading={isLoadingMarketTrends}
                onDateRangeChange={onDateRangeChange}
                onApplyFilter={onApplyFilter}
                onCalendarViewDateChange={onCalendarViewDateChange}
                chartOptions={chartOptions}
                onChartModalOpen={onChartModalOpen}
              />
            </div>

            {/* Row 2: Market Share by Volume, Labeler + Volume by Class of Trade — 50% each */}
            <div className={classes.chartsGridHalf}>
              <MarketShareChart
                marketShareByVolume={marketShareByVolume}
                selectedCompany={selectedCompany}
                onCompanyClick={onMarketShareClick}
                isLoading={isLoadingMarketShare}
              />
              <VolumeByCotChart
                data={volumeByCot}
                isLoading={isLoadingVolumeByCot}
                selectedCompany={selectedCompany}
              />
            </div>
          </div>
        )}
      </div>
    );
  }
);
